import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Box, IconButton, Fade, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import useTaskGuideState from "./useTaskGuideState";
import useUserAPIClient from "../../../api/user/hooks/useUserAPIClient";
import useTaskAPIClient from "../../../api/task/hooks/useTaskAPIClient";
import {
  setMindset as setMindsetAction,
  setSortOrder,
} from "../../../redux/actions/user";
import { setTaskTargeted as setTaskTargetedAction } from "../../../redux/actions/tasks";
import { dispatch as reduxDispatch } from "../../../redux/store";
import sortTasks from "../../../helpers/sort";

import NoTasksStep from "./steps/NoTasksStep";
import AllTargetedStep from "./steps/AllTargetedStep";
import SingleTaskStep from "./steps/SingleTaskStep";
import CapacityStep from "./steps/CapacityStep";
import ApproachStep from "./steps/ApproachStep";
import MoodStep from "./steps/MoodStep";
import EnergyStep from "./steps/EnergyStep";
import TargetStep from "./steps/TargetStep";
import RitualStep from "./steps/RitualStep";
import IntentionStep from "./steps/IntentionStep";
import IntroStep, { INTRO_SLIDES } from "./steps/IntroStep";
import Button from "../../input/button";

// Session storage key for persisting guide state across create-task navigation
const GUIDE_STATE_KEY = "taskGuideState";
// Local storage key to track if the user has seen the intro
const GUIDE_INTRO_SEEN_KEY = "focusGuideIntroSeen";
// Steps that are only valid based on current task count — never restore directly
const GATE_STEPS = ["no-tasks", "single-task", "all-targeted"];

/**
 * Main Task Guide wizard dialog.
 * Orchestrates all wizard steps, navigation, and persistence logic.
 */
const TaskGuideDialog = ({ open, onClose, onOpenTaskHelper }) => {
  const dispatch = useDispatch();
  const { setMindset, setSortPreference } = useUserAPIClient();
  const { setTaskTargeted } = useTaskAPIClient();
  const mindset = useSelector((state) => state.user.mindset);

  const guideState = useTaskGuideState();
  const {
    activeTasks,
    untargetedTasks,
    // Step
    currentStep,
    setCurrentStep,
    initialize,
    reset,
    capacity,
    setCapacity,
    approach,
    setApproach,
    mood,
    setMood,
    energy,
    setEnergy,
    selectedTargets,
    setSelectedTargets,
    toggleTarget,
    taskPage,
    setTaskPage,
    mindsetSaving,
    setMindsetSaving,
    firstActionText,
    setFirstActionText,
    chosenFirstTask,
    setChosenFirstTask,
    mapToMindsetScale,
    getDisplayTaskCount,
    getSuggestedTargetCount,
  } = guideState;

  // Fade transition state
  const [fadeIn, setFadeIn] = useState(true);
  const [pendingStep, setPendingStep] = useState(null);
  const [targetsSaving, setTargetsSaving] = useState(false);

  // Intro slide state
  const [showIntro, setShowIntro] = useState(false);
  const [introSlideIndex, setIntroSlideIndex] = useState(0);

  // Transition between steps with a fade
  const transitionTo = useCallback((step) => {
    setFadeIn(false);
    setPendingStep(step);
  }, []);

  useEffect(() => {
    if (!fadeIn && pendingStep) {
      const timer = setTimeout(() => {
        setCurrentStep(pendingStep);
        setPendingStep(null);
        setFadeIn(true);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [fadeIn, pendingStep, setCurrentStep]);

  // On open, check for saved state or initialize fresh
  useEffect(() => {
    if (open) {
      const saved = sessionStorage.getItem(GUIDE_STATE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Restore all state
          if (parsed.capacity) setCapacity(parsed.capacity);
          if (parsed.approach) setApproach(parsed.approach);
          if (parsed.mood) setMood(parsed.mood);
          if (parsed.energy) setEnergy(parsed.energy);
          if (parsed.selectedTargets)
            setSelectedTargets(parsed.selectedTargets);
          if (parsed.taskPage != null) setTaskPage(parsed.taskPage);
          // Gate steps are task-count-dependent — let initialize recalculate rather than restoring blindly
          const resumeStep = GATE_STEPS.includes(parsed.resumeStep)
            ? null
            : parsed.resumeStep;
          initialize(resumeStep);
          sessionStorage.removeItem(GUIDE_STATE_KEY);
          setShowIntro(false);
        } catch {
          initialize();
        }
      } else {
        // Check if user has seen the intro before
        const introSeen = localStorage.getItem(GUIDE_INTRO_SEEN_KEY);
        if (!introSeen) {
          setShowIntro(true);
          setIntroSlideIndex(0);
        } else {
          setShowIntro(false);
        }
        initialize();
      }
      setFadeIn(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort tasks using the adaptive algorithm (only untargeted tasks for suggestions)
  const sortedTasks = useMemo(() => {
    const tasksArray = [...untargetedTasks];
    return sortTasks({
      tasksToSort: tasksArray,
      sortOrder: "adaptive",
      mindset,
    });
  }, [untargetedTasks, mindset]);

  // Combine existing already-targeted tasks with newly selected targets for the ritual step
  const allTargetTaskObjects = useMemo(() => {
    const alreadyTargeted = activeTasks.filter((t) => t.targeted);
    // Start with already-targeted tasks, then append newly selected ones that aren't already targeted
    const combined = [...alreadyTargeted];
    for (const id of selectedTargets) {
      if (!combined.some((t) => (t.taskId || t.id) === id)) {
        const task = activeTasks.find((t) => (t.taskId || t.id) === id);
        if (task) combined.push(task);
      }
    }
    return combined;
  }, [activeTasks, selectedTargets]);

  // Chosen first task object
  const chosenFirstTaskObj = useMemo(() => {
    if (!chosenFirstTask) return null;
    return activeTasks.find((t) => (t.taskId || t.id) === chosenFirstTask);
  }, [chosenFirstTask, activeTasks]);

  // ─── Save state and open task helper dialog ──────────────────────────
  const saveStateAndOpenHelper = useCallback(
    (resumeStep) => {
      const stateToSave = {
        capacity,
        approach,
        mood,
        energy,
        selectedTargets,
        taskPage,
        resumeStep,
      };
      sessionStorage.setItem(GUIDE_STATE_KEY, JSON.stringify(stateToSave));
      if (onOpenTaskHelper) {
        onOpenTaskHelper();
      }
    },
    [
      capacity,
      approach,
      mood,
      energy,
      selectedTargets,
      taskPage,
      onOpenTaskHelper,
    ],
  );

  // ─── Persist mindset (mood + energy) ──────────────────────────────────
  const persistMindset = useCallback(async () => {
    if (mood === null || energy === null) return;
    setMindsetSaving(true);
    const newMindset = {
      mood: mapToMindsetScale(mood),
      energy: mapToMindsetScale(energy),
    };
    reduxDispatch(setMindsetAction(newMindset));
    await setMindset(newMindset);
    setMindsetSaving(false);
  }, [mood, energy, mapToMindsetScale, setMindset, setMindsetSaving]);

  // ─── Persist sort preference ──────────────────────────────────────────
  const persistApproach = useCallback(
    (selectedApproach) => {
      const newSortOrder =
        selectedApproach === "adaptive" ? "adaptive" : "importance-desc";
      dispatch(setSortOrder(newSortOrder));
      void setSortPreference(newSortOrder);
    },
    [dispatch, setSortPreference],
  );

  // ─── Persist targets ─────────────────────────────────────────────────
  const persistTargets = useCallback(async () => {
    for (const taskId of selectedTargets) {
      reduxDispatch(setTaskTargetedAction({ taskId, targeted: true }));
      await setTaskTargeted(taskId, true);
    }
  }, [selectedTargets, setTaskTargeted]);

  // ─── Step navigation logic ────────────────────────────────────────────
  const handleNext = useCallback(() => {
    switch (currentStep) {
      case "no-tasks":
        // handled by the button going to create page
        break;
      case "single-task":
        // "Start task" skips to first-action
        transitionTo("ritual");
        break;
      case "capacity":
        if (capacity) {
          if (capacity === "single") {
            // Low battery fast-path: set adaptive sort and low mood/energy, skip to targets
            setApproach("adaptive");
            persistApproach("adaptive");
            const lowMindset = { mood: 2, energy: 2 };
            reduxDispatch(setMindsetAction(lowMindset));
            void setMindset(lowMindset);
            setMood(1); // card value 1 maps to scale value 1 (≈2 is close; use 1 for "low")
            setEnergy(1);
            transitionTo("targets");
          } else {
            transitionTo("approach");
          }
        }
        break;
      case "approach":
        if (approach) {
          persistApproach(approach);
          if (approach === "adaptive") {
            transitionTo("mood");
          } else {
            // Skip mood/energy, go to targets
            transitionTo("targets");
          }
        }
        break;
      case "mood":
        if (mood) transitionTo("energy");
        break;
      case "energy":
        if (energy) {
          persistMindset().then(() => {
            transitionTo("targets");
          });
        }
        break;
      case "targets":
        if (selectedTargets.length > 0) {
          setTargetsSaving(true);
          persistTargets().then(() => {
            setTargetsSaving(false);
            // If "Not much" capacity (single) with 1 target, skip ritual and go to intention
            if (capacity === "single" && selectedTargets.length === 1) {
              setChosenFirstTask(selectedTargets[0]);
              transitionTo("intention");
            } else {
              transitionTo("ritual");
            }
          });
        }
        break;
      case "ritual":
        if (chosenFirstTask) transitionTo("intention");
        break;
      case "intention":
        onClose();
        reset();
        break;
      default:
        break;
    }
  }, [
    currentStep,
    capacity,
    approach,
    mood,
    energy,
    selectedTargets,
    chosenFirstTask,
    setApproach,
    setMood,
    setEnergy,
    setMindset,
    persistApproach,
    persistMindset,
    persistTargets,
    transitionTo,
    onClose,
    reset,
  ]);

  const handleBack = useCallback(() => {
    switch (currentStep) {
      case "capacity":
        onClose();
        break;
      case "approach":
        transitionTo("capacity");
        break;
      case "mood":
        transitionTo("approach");
        break;
      case "energy":
        transitionTo("mood");
        break;
      case "targets":
        if (capacity === "single") {
          transitionTo("capacity");
        } else if (approach === "adaptive") {
          transitionTo("energy");
        } else {
          transitionTo("approach");
        }
        break;
      case "ritual":
        transitionTo("targets");
        break;
      case "intention":
        if (capacity === "single") {
          transitionTo("targets");
        } else {
          transitionTo("ritual");
        }
        break;
      default:
        break;
    }
  }, [currentStep, capacity, approach, transitionTo, onClose]);

  // ─── Can proceed? ────────────────────────────────────────────────────
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case "capacity":
        return !!capacity;
      case "approach":
        return !!approach;
      case "mood":
        return mood !== null;
      case "energy":
        return energy !== null;
      case "targets":
        return selectedTargets.length > 0;
      case "ritual":
        return !!chosenFirstTask;
      default:
        return true;
    }
  }, [
    currentStep,
    capacity,
    approach,
    mood,
    energy,
    selectedTargets,
    chosenFirstTask,
  ]);

  // Should show back button?
  const showBack = useMemo(() => {
    if (showIntro) return false;
    return [
      "capacity",
      "approach",
      "mood",
      "energy",
      "targets",
      "ritual",
      "intention",
    ].includes(currentStep);
  }, [currentStep, showIntro]);

  // Should show next button?
  const showNext = useMemo(() => {
    if (showIntro) return false;
    return [
      "capacity",
      "approach",
      "mood",
      "energy",
      "targets",
      "ritual",
    ].includes(currentStep);
  }, [currentStep, showIntro]);

  // Next button label
  const nextLabel = useMemo(() => {
    if (currentStep === "energy") return mindsetSaving ? "" : "Next";
    if (currentStep === "targets") return "Begin";
    if (currentStep === "ritual") return "Next";
    return "Next";
  }, [currentStep, mindsetSaving]);

  // ─── Render ───────────────────────────────────────────────────────────
  const renderStep = () => {
    // Show intro slides on first use
    if (showIntro) {
      return (
        <IntroStep
          slideIndex={introSlideIndex}
          onNext={() => {
            if (introSlideIndex < INTRO_SLIDES.length - 1) {
              setFadeIn(false);
              setTimeout(() => {
                setIntroSlideIndex((prev) => prev + 1);
                setFadeIn(true);
              }, 250);
            } else {
              // Mark intro as seen and proceed to the normal guide
              localStorage.setItem(GUIDE_INTRO_SEEN_KEY, "true");
              setFadeIn(false);
              setTimeout(() => {
                setShowIntro(false);
                setFadeIn(true);
              }, 250);
            }
          }}
        />
      );
    }

    switch (currentStep) {
      case "no-tasks":
        return (
          <NoTasksStep
            onNavigateToCreate={() => saveStateAndOpenHelper("no-tasks")}
          />
        );

      case "all-targeted":
        return (
          <AllTargetedStep
            onCreateMore={() => saveStateAndOpenHelper("all-targeted")}
          />
        );

      case "single-task":
        return (
          <SingleTaskStep
            task={activeTasks[0]}
            onCreateMore={() => saveStateAndOpenHelper("single-task")}
            onStartTask={() => {
              // Target the single task, then skip to first action step
              const taskId = activeTasks[0].taskId || activeTasks[0].id;
              setSelectedTargets([taskId]);
              setChosenFirstTask(taskId);
              reduxDispatch(setTaskTargetedAction({ taskId, targeted: true }));
              void setTaskTargeted(taskId, true);
              transitionTo("intention");
            }}
          />
        );

      case "capacity":
        return (
          <CapacityStep
            selected={capacity}
            onSelect={(val) => {
              setCapacity(val);
            }}
          />
        );

      case "approach":
        return (
          <ApproachStep
            selected={approach}
            onSelect={(val) => {
              setApproach(val);
            }}
          />
        );

      case "mood":
        return <MoodStep selected={mood} onSelect={(val) => setMood(val)} />;

      case "energy":
        return (
          <EnergyStep
            selected={energy}
            onSelect={(val) => setEnergy(val)}
            loading={mindsetSaving}
          />
        );

      case "targets":
        return (
          <TargetStep
            sortedTasks={sortedTasks}
            selectedTargets={selectedTargets}
            onToggleTarget={toggleTarget}
            suggestedTargetCount={getSuggestedTargetCount()}
            taskPage={taskPage}
            onPageChange={setTaskPage}
            onCreateNewTask={() => saveStateAndOpenHelper("targets")}
            displayTaskCount={getDisplayTaskCount()}
            capacity={capacity}
          />
        );

      case "ritual":
        return (
          <RitualStep
            targetTasks={allTargetTaskObjects}
            chosenTask={chosenFirstTask}
            onChooseTask={setChosenFirstTask}
          />
        );

      case "intention":
        return (
          <IntentionStep
            taskName={chosenFirstTaskObj?.name || "your task"}
            value={firstActionText}
            onChange={setFirstActionText}
            onFinish={() => {
              onClose();
              reset();
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        className: "task-guide-dialog",
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      <Box className="task-guide-dialog__header">
        {showBack && (
          <IconButton
            onClick={handleBack}
            className="task-guide-dialog__back"
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <IconButton
          onClick={() => {
            onClose();
          }}
          className="task-guide-dialog__close"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className="task-guide-dialog__body scroller">
        <Fade in={fadeIn} timeout={250}>
          <Box className="task-guide-dialog__step-container">
            {renderStep()}
          </Box>
        </Fade>
      </Box>

      {showNext && (
        <Box className="task-guide-dialog__footer">
          {currentStep === "targets" ? (
            <Box
              className={`task-guide-step__begin-button ${!canProceed || targetsSaving ? "task-guide-step__begin-button--disabled" : ""}`}
              onClick={canProceed && !targetsSaving ? handleNext : undefined}
            >
              {targetsSaving ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Begin"
              )}
            </Box>
          ) : (
            <Button
              text={nextLabel}
              onClick={handleNext}
              disabled={!canProceed}
              loading={mindsetSaving}
            />
          )}
        </Box>
      )}
    </Dialog>
  );
};

export default TaskGuideDialog;
